import { Fragment, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { InfoEditCell } from './InfoEditCell';
import { useLanguage } from '../../contexts/LanguageContext';
import { setInfoField, useBrand } from './brandStore';
import { useListing } from './listingStore';
import { type Choice, type InfoField, type Source, pickInfo, useResolvedInfo } from './infoPicks';
import { formatBrand } from './formatNames';
import { formatTime, formatWeight } from './formatUnits';
import { localizeCategory, localizeCategoryOrBlank, useCategories } from './catalogCategories';
import { PICKED } from './pickedLook';
import { useHeroCrop } from './heroCrop';

// The manual's general info: one column per field, a header row over a value row.
//
// One of these fields can have TWO answers — the manual's pages and the web listing both
// name the product, and they don't always agree. Where they disagree both answers are shown
// in the cell, labelled with where each came from, and the user clicks the one to keep. Nothing resolves this for them: Phase 1
// stays open until every disagreement is settled (see infoPicks).

// One candidate, as a button. The picked one is outlined; while nothing is picked
// both sit neutral and the cell is ringed red, so an unanswered question is
// visible at a glance rather than hidden behind a default.
function Candidate({
  source,
  text,
  picked,
  onPick,
}: {
  source: Source;
  text: string;
  picked: boolean;
  onPick: () => void;
}) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={picked}
      className={`flex min-w-0 flex-1 flex-col items-center rounded-md border-2 px-2 py-0.5 transition-colors ${
        picked
          ? PICKED
          : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'
      }`}
    >
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
        {t(`notice.source.${source}`)}
      </span>
      <span className="max-w-full break-words font-semibold leading-tight text-slate-900">{text}</span>
    </button>
  );
}

// Each column's width, in rem: room for its values, with a header allowed two lines ("Max /
// weight"). Narrow on purpose, so every column fits the panel side by side and the table
// never scrolls; a value longer than its column wraps instead. On a narrower panel the
// whole table shrinks with it, each column keeping its share.
// How many search phrases "Find the links" types: the server's _MAX_QUERIES.
const MAX_QUERIES = 3;

const COL_REM: Record<string, number> = {
  brand: 6.5, productName: 6.5, time: 6, people: 6, maxWeight: 5, category: 6, site: 8,
  queries: 8.5, hero: 6,
};

// A value cell: the plain value, or — where the manual and the listing disagree — BOTH
// candidates, one over the other, each labelled with its source. They stay buttons after the
// pick, picked or not, so changing your mind needs no re-run.
function ChoiceCell({
  docId,
  field,
  choice,
  children,
}: {
  docId: string;
  field: InfoField;
  choice: Choice<string>;
  // What to render when there is nothing to choose: the editable value.
  children: ReactNode;
}) {
  if (!choice.conflict) return <>{children}</>;
  return (
    <div
      className={`flex w-full min-w-0 flex-col gap-1 rounded-lg p-1 ${
        choice.pending ? 'ring-2 ring-rose-400' : ''
      }`}
    >
      {(['pdf', 'listing'] as Source[]).map((side) => (
        <Candidate
          key={side}
          source={side}
          text={choice[side]}
          picked={choice.pick === side}
          onPick={() => pickInfo(docId, field, side)}
        />
      ))}
    </div>
  );
}

type InfoCol = { key: string; label: string; busy: boolean; content: ReactNode };

// The one-row table both subphases show their product fields in: a header row over a
// value row, at FIXED WIDTHS, so the columns hold still when the language changes. Sized
// to the content, the table followed its headers: "Personnes" is wider than "People", and
// every toggle between FR and EN moved every column.
function InfoGrid({ cols }: { cols: InfoCol[] }) {
  const total = cols.reduce((sum, c) => sum + COL_REM[c.key], 0);
  return (
    <div className="flex w-full justify-center">
      <div
        className="overflow-hidden rounded-xl border-2 border-slate-300 bg-white shadow-sm"
        style={{ width: `${total}rem`, maxWidth: '100%' }}
      >
        <table className="w-full table-fixed text-center text-sm">
          <colgroup>
            {cols.map((c) => <col key={c.key} style={{ width: `${(100 * COL_REM[c.key]) / total}%` }} />)}
          </colgroup>
          <tbody>
            <tr className="border-b-2 border-slate-300">
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="border-r-2 border-slate-300 px-2 py-1.5 font-semibold leading-tight text-slate-900 last:border-r-0"
                >
                  {c.label}
                </th>
              ))}
            </tr>
            <tr>
              {cols.map((c) => (
                <td
                  key={c.key}
                  className="border-r-2 border-slate-300 px-2 py-1.5 align-middle text-slate-700 last:border-r-0"
                >
                  {c.busy ? (
                    <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-900" />
                  ) : (
                    c.content
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const plain = (v: string) => <span className="font-semibold text-slate-900">{v.trim()}</span>;

// THE PRODUCT'S TABLE: its fields, one row, under the PDF most of them are read off (see
// UploadPanel). The product name has a second source, the listings: it spins while either
// is being read, and shows both answers where they disagree. The listings' materials are a
// column of the Listings table.
export function NoticeInfoTable({ docId, file }: { docId: string; file: Blob }) {
  const { t, language } = useLanguage();
  const { time, people, maxWeight, category, site, queries, detecting, raw } = useBrand(docId);
  const { brand, productName } = useResolvedInfo(docId);
  const listing = useListing(docId);
  const hero = useHeroCrop(docId, file);
  const cats = useCategories();

  // The category is stored as its English canonical name; show its French label
  // (from the Parts DB ontology) when the app is in FR — or nothing, if the ontology
  // has no French name for it. Better an empty cell than an English word on a French
  // page: the empty cell is what says the ontology is missing a translation.
  const values = {
    brand: formatBrand(brand.pdf),
    productName: productName.pdf,
    time: formatTime(time),
    people,
    maxWeight: formatWeight(maxWeight),
    category: localizeCategoryOrBlank(cats, category, language),
    site,
    queries: queries.join(', '),
    hero: hero ?? '',
  };

  const hasInfo = detecting || !!raw.trim() || Object.values(values).some((v) => v.trim());
  if (!hasInfo) return null;

  // NO REASONING BADGES BESIDE THIS TABLE. There were two — one per model call, one
  // captioned MANUAL and one LISTING — hung off the table's right edge. They are gone
  // from the app: what they opened is the call's thinking, which is a developer's
  // question asked next to an answer a reader is reading.
  const col = (key: keyof typeof values, label: string, content: ReactNode = plain(values[key])): InfoCol =>
    ({ key, label, busy: detecting, content });
  // EVERY FIELD IS EDITED BY CLICKING IT (InfoEditCell). The product name is the manual's
  // answer of a pair: typing it settles any disagreement with the listing in its favour,
  // since having just written the value, the user is not owed the question.
  const edit = (
    key: keyof typeof values, stored: string, save: (v: string) => void,
    shown: ReactNode = plain(values[key]),
    extra: Partial<Parameters<typeof InfoEditCell>[0]> = {},
  ) => (
    <InfoEditCell value={stored} onSave={save} title={t('notice.info.edit')} {...extra}>{shown}</InfoEditCell>
  );
  const products = cats.filter((c) => (c.parent || '').toLowerCase() === 'components');

  return (
    <InfoGrid
      cols={[
        col('brand', t('notice.step3.brand'), edit('brand', brand.pdf, (v) => setInfoField(docId, { brand: v }))),
        {
          ...col('productName', t('notice.info.productName'), (
            <ChoiceCell docId={docId} field="productName" choice={productName}>
              {edit('productName', productName.value, (v) => {
                setInfoField(docId, { productName: v });
                pickInfo(docId, 'productName', 'pdf');
              }, plain(productName.value))}
            </ChoiceCell>
          )),
          busy: detecting || listing.detecting,
        },
        col('time', t('notice.info.time'), edit('time', time, (v) => setInfoField(docId, { time: v }))),
        col('people', t('notice.info.people'), edit('people', people, (v) => setInfoField(docId, { people: v }))),
        col('maxWeight', t('notice.info.maxWeight'),
          edit('maxWeight', maxWeight, (v) => setInfoField(docId, { maxWeight: v }))),
        col('category', t('notice.info.category'), edit(
          'category', category, (v) => setInfoField(docId, { category: v }), undefined,
          { options: products.map((c) => ({ value: c.name, label: localizeCategory(cats, c.name, language) })) },
        )),
        col('site', t('notice.info.site'), edit('site', site, (v) => setInfoField(docId, { site: v }), (
          <span className="font-semibold text-slate-900 [overflow-wrap:anywhere]">
            {/* Wrapped after a dot, where a host reads as two pieces, not mid-word. */}
            {site.split(/(?<=\.)/).map((part, i) => <Fragment key={i}>{part}<wbr /></Fragment>)}
          </span>
        ))),
        col('hero', t('notice.info.hero'), hero && (
          <img src={hero} alt={t('notice.info.hero')} className="mx-auto max-h-20 max-w-full object-contain" />
        )),
        // One phrase a line, ruled apart like the Listings table's materials: a phrase that
        // wraps onto two lines would otherwise run into the next one. EACH EDITED ON ITS OWN:
        // emptied, a phrase is dropped; the blank line under them adds one, up to the
        // search's three (MAX_QUERIES on the server).
        col('queries', t('notice.info.queries'), (
          <div className="flex flex-col divide-y divide-slate-200 leading-snug">
            {[...queries, ...(queries.length < MAX_QUERIES ? [''] : [])].map((q, i) => (
              <div key={i} className="py-0.5 first:pt-0 last:pb-0">
                <InfoEditCell
                  value={q}
                  title={t('notice.info.edit')}
                  onSave={(v) => {
                    const next = [...queries];
                    next[i] = v;
                    setInfoField(docId, { queries: next.map((x) => x.trim()).filter(Boolean) });
                  }}
                >
                  {plain(q)}
                </InfoEditCell>
              </div>
            ))}
          </div>
        )),
      ]}
    />
  );
}
